import {
  Cancel01Icon,
  FireIcon,
  LockIcon,
  PlayCircleIcon,
  StarIcon,
  Tick02Icon
} from '@hugeicons/react'
import clsx from 'clsx'
import type { ReactNode } from 'react'

/* `available` (antes `nova`) é disponibilidade: liberado, ainda não iniciado.
   `new` é recência: publicado dentro da janela de `shared/lib/recency`. Eram a
   mesma variante, e por isso a estrela carimbava todo item disponível desde o
   primeiro minuto do evento. São eixos independentes — um item pode ser os dois. */
export type BadgeStatusVariant =
  | 'available'
  | 'new'
  | 'progress'
  | 'success'
  | 'danger'
  | 'locked'
  | 'expired'

export type BadgeSize = 'sm' | 'md'

/** `solid` = fundo cheio (default). `light` = fundo claro + texto na cor do status.
 *  `ghost` = só texto na cor semântica — rótulos de categoria/tipo em cards. */
export type BadgeTone = 'solid' | 'light' | 'ghost'

const ICON_SIZE: Record<BadgeSize, number> = {
  sm: 11,
  md: 13
}

const defaultStatusIcon = (
  variant: BadgeStatusVariant,
  size: BadgeSize
): ReactNode => {
  const px = ICON_SIZE[size]
  const icons: Record<BadgeStatusVariant, ReactNode> = {
    /* A estrela é da recência, não da disponibilidade. */
    available: <PlayCircleIcon size={px} variant="duotone" aria-hidden />,
    new: <StarIcon size={px} variant="duotone" aria-hidden />,
    progress: <FireIcon size={px} variant="duotone" aria-hidden />,
    success: <Tick02Icon size={px} variant="stroke" aria-hidden />,
    danger: <Cancel01Icon size={px} variant="stroke" aria-hidden />,
    locked: <LockIcon size={px} variant="duotone" aria-hidden />,
    expired: <Cancel01Icon size={px} variant="stroke" aria-hidden />
  }
  return icons[variant]
}

export type BadgeProps = {
  variant: BadgeStatusVariant
  size?: BadgeSize
  tone?: BadgeTone
  /** Default false — mockup de listagem usa só texto. */
  showIcon?: boolean
  icon?: ReactNode
  children: ReactNode
  className?: string
}

/**
 * Status gamificado sólido — pill `999px`, fundo cheio, texto branco, sem borda.
 * Cor = semântica fixa de estado, nunca escolha estética: `available` roxo,
 * `progress` laranja, `success` verde, `danger` vermelho, `locked` cinza,
 * `expired` vermelho. `new` é a exceção de forma: outline, sem preenchimento —
 * distingue recência de disponibilidade sem gastar um matiz novo.
 * `size="sm"` (18px) em cards de listagem, `md` (22px) standalone. Sem animação
 * de pulse (removida — reduzia acessibilidade/distração visual).
 *
 * @example
 * <Badge variant="progress">Em andamento</Badge>
 * <Badge variant="success" tone="light">Concluída</Badge>
 * <Badge variant="available" tone="ghost" showIcon={false}>Patrocinador</Badge>
 */
export const Badge = ({
  variant,
  size = 'sm',
  tone = 'solid',
  showIcon = false,
  icon,
  children,
  className
}: BadgeProps) => {
  const iconNode = showIcon ? (icon ?? defaultStatusIcon(variant, size)) : null

  return (
    <span
      className={clsx(
        'badge',
        `badge-${variant}`,
        tone === 'light' && 'badge-tone-light',
        tone === 'ghost' && 'badge-tone-ghost',
        size === 'md' && 'badge-md',
        className
      )}
    >
      {iconNode && (
        <span className="inline-flex items-center">{iconNode}</span>
      )}
      <span>{children}</span>
    </span>
  )
}

Badge.displayName = 'Badge'
